import { Injectable, inject } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { ClientService } from './client.service';

export enum FlashcardAssetScope {
  Front = 0,
  Back = 1,
  Explanation = 2,
  BackOption = 3,
  Media = 4,
}

export enum FlashcardAssetOrigin {
  UserAsset = 0,
  GeneratedTts = 1,
}

export interface FlashcardAssetModel {
  id?: number;
  workspaceId?: number;
  flashcardId?: number;
  workspaceAssetId?: number;
  assetType?: number;
  assetScope?: number;
  assetOrigin?: number;
  targetId?: number | null;
  jobId?: number | null;
  fileName?: string | null;
  mimeType?: string | null;
  fileUrl?: string | null;
  altText?: string | null;
  fileSizeBytes?: number | null;
  durationSeconds?: number | null;
  isActive?: boolean | null;
}

export type FlashcardAssetUpsertPayload = {
  id?: number;
  flashcardId: number;
  workspaceAssetId?: number;
  assetType: number;
  assetScope: number;
  targetId?: number | null;
  fileName?: string;
  mimeType?: string;
  fileUrl?: string;
  altText?: string | null;
  fileSizeBytes?: number | null;
  durationSeconds?: number | null;
  isActive?: boolean | null;
};

export type FlashcardImageUploadPayload = {
  flashcardId: number;
  assetScope: number;
  file: File;
  altText?: string | null;
  replaceAssetId?: number | null;
  targetId?: number | null;
};

export type FlashcardVideoUploadPayload = {
  flashcardId: number;
  assetScope: number;
  file: File;
  altText?: string | null;
  replaceAssetId?: number | null;
  targetId?: number | null;
};

export type FlashcardAudioUploadPayload = {
  flashcardId: number;
  assetScope: number;
  file: File;
  altText?: string | null;
  replaceAssetId?: number | null;
  targetId?: number | null;
};

export type FlashcardAssetCopyPayload = {
  flashcardId: number;
  workspaceAssetId: number;
  assetType: number;
  assetScope: number;
  altText?: string | null;
  replaceAssetId?: number | null;
  targetId?: number | null;
};

export type FlashcardAssetBrowserUrl = {
  url: string;
  fileUrl: string;
  isObjectUrl: boolean;
};

type FlashcardAssetResponse =
  | FlashcardAssetModel
  | {
      data?: FlashcardAssetModel;
      item?: FlashcardAssetModel;
      value?: FlashcardAssetModel;
    };

type FlashcardAssetsResponse =
  | FlashcardAssetModel[]
  | {
      data?: FlashcardAssetModel[];
      items?: FlashcardAssetModel[];
      value?: FlashcardAssetModel[];
    };

const FLASHCARD_ASSET_ROUTES = {
  workspaceImages: 'flashcard-asset/images',
  flashcardAssets: (flashcardId: number) => `flashcard/${flashcardId}/assets`,
  createOrUpdate: 'flashcard-asset',
  deleteById: (id: number) => `flashcard-asset/${id}`,
  uploadImage: (flashcardId: number) => `flashcard/${flashcardId}/assets/image`,
  uploadVideo: (flashcardId: number) => `flashcard/${flashcardId}/assets/video`,
  uploadAudio: (flashcardId: number) => `flashcard/${flashcardId}/assets/audio`,
  attachFromLibrary: (flashcardId: number) =>
    `flashcard/${flashcardId}/assets/from-library`,
};

@Injectable({ providedIn: 'root' })
export class FlashcardAssetService {
  private readonly client = inject(ClientService);

  listWorkspaceImages(): Observable<FlashcardAssetModel[]> {
    return this.client
      .get<FlashcardAssetsResponse>(FLASHCARD_ASSET_ROUTES.workspaceImages)
      .pipe(map((response) => this.unwrapList(response)));
  }

  listByFlashcard(flashcardId: number): Observable<FlashcardAssetModel[]> {
    return this.client
      .get<FlashcardAssetsResponse>(
        FLASHCARD_ASSET_ROUTES.flashcardAssets(flashcardId),
      )
      .pipe(map((response) => this.unwrapList(response)));
  }

  create(
    payload: FlashcardAssetUpsertPayload,
  ): Observable<FlashcardAssetModel> {
    return this.client
      .post<
        FlashcardAssetResponse,
        FlashcardAssetUpsertPayload
      >(FLASHCARD_ASSET_ROUTES.createOrUpdate, payload)
      .pipe(map((response) => this.unwrapItem(response)));
  }

  update(
    payload: FlashcardAssetUpsertPayload,
  ): Observable<FlashcardAssetModel> {
    return this.client
      .put<
        FlashcardAssetResponse,
        FlashcardAssetUpsertPayload
      >(FLASHCARD_ASSET_ROUTES.createOrUpdate, payload)
      .pipe(map((response) => this.unwrapItem(response)));
  }

  delete(id: number): Observable<boolean> {
    return this.client
      .delete<unknown>(FLASHCARD_ASSET_ROUTES.deleteById(id))
      .pipe(
        map((response) => {
          if (typeof response === 'boolean') {
            return response;
          }

          if (typeof (response as { data?: unknown })?.data === 'boolean') {
            return Boolean((response as { data?: unknown }).data);
          }

          if (typeof (response as { value?: unknown })?.value === 'boolean') {
            return Boolean((response as { value?: unknown }).value);
          }

          return true;
        }),
      );
  }

  uploadImage(
    payload: FlashcardImageUploadPayload,
  ): Observable<FlashcardAssetModel> {
    const formData = new FormData();
    formData.append('file', payload.file, payload.file.name);
    formData.append('mimeType', payload.file.type);
    formData.append('assetScope', String(payload.assetScope));

    const altText = payload.altText?.trim();
    if (altText) {
      formData.append('altText', altText);
    }

    if (payload.replaceAssetId != null && payload.replaceAssetId > 0) {
      formData.append('replaceAssetId', String(payload.replaceAssetId));
    }
    if (payload.targetId != null && payload.targetId > 0) {
      formData.append('targetId', String(payload.targetId));
    }

    return this.client
      .post<
        FlashcardAssetResponse,
        FormData
      >(FLASHCARD_ASSET_ROUTES.uploadImage(payload.flashcardId), formData)
      .pipe(map((response) => this.unwrapItem(response)));
  }

  uploadVideo(
    payload: FlashcardVideoUploadPayload,
  ): Observable<FlashcardAssetModel> {
    const formData = new FormData();
    formData.append('file', payload.file, payload.file.name);
    formData.append('mimeType', payload.file.type);
    formData.append('assetScope', String(payload.assetScope));

    const altText = payload.altText?.trim();
    if (altText) {
      formData.append('altText', altText);
    }

    if (payload.replaceAssetId != null && payload.replaceAssetId > 0) {
      formData.append('replaceAssetId', String(payload.replaceAssetId));
    }
    if (payload.targetId != null && payload.targetId > 0) {
      formData.append('targetId', String(payload.targetId));
    }

    return this.client
      .post<
        FlashcardAssetResponse,
        FormData
      >(FLASHCARD_ASSET_ROUTES.uploadVideo(payload.flashcardId), formData)
      .pipe(map((response) => this.unwrapItem(response)));
  }

  uploadAudio(
    payload: FlashcardAudioUploadPayload,
  ): Observable<FlashcardAssetModel> {
    const formData = new FormData();
    formData.append('file', payload.file, payload.file.name);
    formData.append('mimeType', payload.file.type);
    formData.append('assetScope', String(payload.assetScope));

    const altText = payload.altText?.trim();
    if (altText) {
      formData.append('altText', altText);
    }

    if (payload.replaceAssetId != null && payload.replaceAssetId > 0) {
      formData.append('replaceAssetId', String(payload.replaceAssetId));
    }
    if (payload.targetId != null && payload.targetId > 0) {
      formData.append('targetId', String(payload.targetId));
    }

    return this.client
      .post<
        FlashcardAssetResponse,
        FormData
      >(FLASHCARD_ASSET_ROUTES.uploadAudio(payload.flashcardId), formData)
      .pipe(map((response) => this.unwrapItem(response)));
  }

  attachFromLibrary(
    payload: FlashcardAssetCopyPayload,
  ): Observable<FlashcardAssetModel> {
    return this.client
      .post<
        FlashcardAssetResponse,
        Omit<FlashcardAssetCopyPayload, 'flashcardId'>
      >(FLASHCARD_ASSET_ROUTES.attachFromLibrary(payload.flashcardId), {
        workspaceAssetId: payload.workspaceAssetId,
        assetType: payload.assetType,
        assetScope: payload.assetScope,
        targetId: payload.targetId ?? null,
        altText: payload.altText?.trim() || null,
        replaceAssetId: payload.replaceAssetId ?? null,
      })
      .pipe(map((response) => this.unwrapItem(response)));
  }

  resolveBrowserUrl(
    asset: FlashcardAssetModel,
  ): Observable<FlashcardAssetBrowserUrl> {
    const fileUrl = this.toString(asset.fileUrl).trim();

    if (!fileUrl) {
      return of({ url: '', fileUrl: '', isObjectUrl: false });
    }

    if (this.isBrowserRenderableUrl(fileUrl)) {
      return of({
        url: fileUrl,
        fileUrl,
        isObjectUrl: fileUrl.startsWith('blob:'),
      });
    }

    return of({ url: '', fileUrl, isObjectUrl: false });
  }

  private unwrapList(response: FlashcardAssetsResponse): FlashcardAssetModel[] {
    if (Array.isArray(response)) {
      return response.map((item) => this.normalizeAsset(item));
    }

    if (Array.isArray(response?.data)) {
      return response.data.map((item) => this.normalizeAsset(item));
    }

    if (Array.isArray(response?.items)) {
      return response.items.map((item) => this.normalizeAsset(item));
    }

    if (Array.isArray(response?.value)) {
      return response.value.map((item) => this.normalizeAsset(item));
    }

    return [];
  }

  private unwrapItem(response: FlashcardAssetResponse): FlashcardAssetModel {
    const wrapped = response as Record<string, unknown>;
    const data = wrapped['data'];
    const item = wrapped['item'];
    const value = wrapped['value'];

    if (this.isModel(data)) {
      return this.normalizeAsset(data);
    }

    if (this.isModel(item)) {
      return this.normalizeAsset(item);
    }

    if (this.isModel(value)) {
      return this.normalizeAsset(value);
    }

    if (this.isModel(response)) {
      return this.normalizeAsset(response);
    }

    return {};
  }

  private isModel(value: unknown): value is FlashcardAssetModel {
    if (typeof value !== 'object' || value === null) {
      return false;
    }

    const record = value as Record<string, unknown>;
    return 'id' in record || 'flashcardId' in record;
  }

  private normalizeAsset(asset: FlashcardAssetModel): FlashcardAssetModel {
    const normalizedAssetType = this.toNumber(asset.assetType);
    const normalizedAssetScope = this.toNumber(asset.assetScope);
    const normalizedAssetOrigin = this.toNumber(asset.assetOrigin);

    return {
      id: asset.id,
      workspaceId: asset.workspaceId,
      flashcardId: asset.flashcardId,
      workspaceAssetId: asset.workspaceAssetId,
      assetType: normalizedAssetType,
      assetScope: normalizedAssetScope,
      assetOrigin: normalizedAssetOrigin,
      targetId: asset.targetId,
      jobId: asset.jobId,
      fileName: asset.fileName,
      mimeType: asset.mimeType,
      fileUrl: asset.fileUrl,
      altText: asset.altText,
      fileSizeBytes: asset.fileSizeBytes,
      durationSeconds: asset.durationSeconds,
      isActive: asset.isActive,
    };
  }

  private isBrowserRenderableUrl(url: string): boolean {
    return /^(https?:|blob:|data:)/i.test(url);
  }

  private toString(value: string | null | undefined): string {
    return value ?? '';
  }

  private toNumber(value: unknown): number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    return undefined;
  }
}
